import React from 'react';
import {
  Modal,
  ModalHeader,
  ModalSection,
  ModalFooter,
  Paragraph,
  Gutter,
  Dropdown,
  Input,
  CondensedSmall,
  Button,
  Form,
  Grid,
  GridRow,
  GridColumn,
} from '@axiom/ui';
import type {
  MfaPhoneNumber,
  MfaPhoneNumberPreferences,
  User,
} from '@axiom/validation';
import { CountryCodesDropdownOptions } from '@axiom/const';

import { UsersApi } from '../../api/users-api';

import { PreferencesMfaPhoneAddModalFormSchema } from './preferences-mfa-phone-add-modal-form-schema';

export type PreferencesMfaPhoneAddModalProps = {
  mfaPhoneNumber: MfaPhoneNumberPreferences;
  name?: string;
  onAfterSubmit: () => void;
  onClose: () => void;
  userId: User['id'];
  watchPhoneNumber: (value: MfaPhoneNumber) => void;
};

export const PreferencesMfaPhoneAddModal = ({
  name = 'PREFERENCES_MFA_PHONE_ADD_MODAL',
  onClose,
  onAfterSubmit,
  watchPhoneNumber,
  userId,
  mfaPhoneNumber,
}: PreferencesMfaPhoneAddModalProps) => {
  return (
    <Form
      name={name}
      schema={PreferencesMfaPhoneAddModalFormSchema}
      initialValues={{
        country: mfaPhoneNumber.notificationsPhoneNumberCountry ?? 'US',
        countryCode: mfaPhoneNumber.notificationsPhoneNumberCountryCode ?? '1',
        phoneNumber: mfaPhoneNumber.notificationsPhoneNumber ?? undefined,
      }}
      onSubmit={async (changedData, actions, values) => {
        watchPhoneNumber({
          phoneNumber: values.phoneNumber,
          phoneNumberCountry: values.country,
          phoneNumberCountryCode: values.countryCode,
          registeredForMfa: null,
        });
        await UsersApi.updateMfaPhoneNumber(userId, {
          phoneNumber: values.phoneNumber,
          phoneNumberCountry: values.country,
          phoneNumberCountryCode: values.countryCode,
          registeredForMfa: null,
        });
        onAfterSubmit();
      }}
    >
      {({ fireSubmit, values, setValues }) => {
        return (
          <Modal>
            <ModalHeader
              name={`${name}_HEADER`}
              onClose={() => {
                onClose();
              }}
            >
              Add phone number
            </ModalHeader>
            <ModalSection disableScrolling>
              <Paragraph>
                Provide a mobile phone number we can use to verify it’s you.
                Please enter this number with no dashes, spaces or parentheses.
              </Paragraph>
              <Gutter bottom="16px" />
              <Grid>
                <GridRow>
                  <GridColumn width={4}>
                    <Dropdown
                      displayKey="display"
                      name="country"
                      options={CountryCodesDropdownOptions}
                      valueKey="addressCountryCode"
                      label="Country code"
                      onChange={(value, e, valueOption) => {
                        const option =
                          valueOption as (typeof CountryCodesDropdownOptions)[number];

                        void setValues({
                          ...values,
                          country: option.addressCountryCode,
                          countryCode: option.value,
                        });
                      }}
                    />
                  </GridColumn>
                  <GridColumn width={8}>
                    <Input name="phoneNumber" label="Mobile phone number" />
                  </GridColumn>
                </GridRow>
              </Grid>
              <Gutter bottom="16px" />
              <CondensedSmall>
                By continuing, you authorize Axiom to send security codes and
                other Axiom communications via SMS. Carrier fees may apply. You
                can update your communication preferences at any time.
              </CondensedSmall>
            </ModalSection>
            <ModalFooter>
              <Button
                name="CANCEL_BUTTON"
                variation="outline"
                onClick={() => {
                  onClose();
                }}
              >
                Cancel
              </Button>
              <Button name="SUBMIT_BUTTON" onClick={fireSubmit}>
                Send Code
              </Button>
            </ModalFooter>
          </Modal>
        );
      }}
    </Form>
  );
};
